/**
 * `<AiEvaluationTab>` — orchestrator for the AI Evaluation tab content
 * (Batch 30j-3).
 *
 * Owns the state machine:
 *   - top action bar (JD context line + Generate — first-run only)
 *   - phase derivation via `derivePhase` (lib/derivePhase.ts)
 *   - composition of `<EvaluationCards>` / `<EvaluationCardsSkeleton>` /
 *     empty / generating / error / no-jd states
 *
 * Spec: `frontend/docs/30j_3_design.md` §2 + §3 + §7 +
 * `frontend/docs/30j_3_architect.md` §5.1 + §3 + §4 + §5.4.
 *
 * LCP guarantee (Sec HARD-4 / S-30j3-3 / S-30j3-8): the `useInterview-
 * Analysis` query is gated on `enabled: activeTab === "eval"`. When
 * the user is on `?tab=resume`, the queryFn is NEVER invoked. Polling
 * pauses on tab deactivation; resumes on tab activation.
 *
 * No URL state for active section (Architect §3.3). Active section is
 * `useState` inside `<EvaluationCards>`.
 *
 * Regenerate removed (auto-eval-prep): the AI Evaluation artifact is
 * deterministic. Once generated, no re-run affordance is surfaced.
 */
import { useMemo, type JSX, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Briefcase,
  Sparkles,
} from "lucide-react";

import { Button } from "@/components/common/Button";

import { EvaluationCards } from "@/components/candidate-page/EvaluationCards";
import { EvaluationCardsSkeleton } from "@/components/candidate-page/EvaluationCardsSkeleton";
import { EvaluationProgressBanner } from "@/components/candidate-page/EvaluationProgressBanner";
import { resolveActiveTab } from "@/components/candidate-page/tabs";

import { useInterviewAnalysis } from "@/hooks/queries/interview-analysis/useInterviewAnalysis";
import { useStartInterviewAnalysis } from "@/hooks/queries/interview-analysis/useStartInterviewAnalysis";
import type { Candidate } from "@/hooks/queries/candidates/useCandidates";
import { parseEvaluation } from "@/lib/parseEvaluation";
import { derivePhase } from "@/lib/derivePhase";
import { AiRatingWidget } from "@/components/ai/AiRatingWidget";

interface JdSummary {
  id: string;
  name: string;
}

interface AiEvaluationTabProps {
  candidate: Candidate;
  jdId: string | undefined;
  jds: JdSummary[];
}

export function AiEvaluationTab({
  candidate,
  jdId,
  jds,
}: AiEvaluationTabProps): JSX.Element {
  const [searchParams] = useSearchParams();
  const activeTab = resolveActiveTab(searchParams.get("tab"));

  // Pre-evaluation query — the canonical row for THIS tab.
  const evalQ = useInterviewAnalysis({
    candidateId: candidate.id,
    type: "pre",
    jdId,
    enabled: activeTab === "eval",
  });

  const startMutation = useStartInterviewAnalysis();

  const phase = derivePhase({ jdId, query: evalQ });
  const row = evalQ.data ?? null;

  // Memoize parser invocation on the raw analysis string.
  const parsed = useMemo(
    () => (row?.analysis ? parseEvaluation(row.analysis) : null),
    [row?.analysis],
  );

  // Derived JD context.
  const jdName = jds.find((j) => j.id === jdId)?.name;

  const isGenerating = phase === "generating" || startMutation.isPending;

  // Surface the top-bar Generate affordance ONLY in first-run states.
  // Hide it when an analysis already exists (`loaded`) AND while the query
  // is still settling (`loading`): during loading we don't yet know whether
  // an analysis exists, so rendering Generate would flash it for a frame
  // before existing content lands (founder bug FIX 1). It stays visible for
  // no-jd (disabled), empty, generating, and error.
  const showTopBarGenerate = phase !== "loaded" && phase !== "loading";

  const onGenerate = (): void => {
    if (!jdId) return;
    startMutation.mutate({
      candidate_id: candidate.id,
      type: "pre",
      jd_id: jdId,
    });
  };

  return (
    <div
      className="flex flex-1 flex-col overflow-hidden"
      data-testid="ai-evaluation-tab"
    >
      <EvaluationTopBar
        jdName={jdName}
        showGenerate={showTopBarGenerate}
        isGenerating={isGenerating}
        canGenerate={!!jdId}
        onGenerate={onGenerate}
      />
      <div
        className="scroll-primary flex flex-1 flex-col overflow-x-hidden overflow-y-auto"
        data-testid="ai-eval-content"
      >
        {phase === "no-jd" ? <NoJdEmptyState /> : null}
        {phase === "loading" ? <EvaluationCardsSkeleton /> : null}
        {phase === "empty" ? (
          <EmptyState
            jdName={jdName}
            canGenerate={!!jdId}
            onGenerate={onGenerate}
          />
        ) : null}
        {phase === "generating" ? (
          <GeneratingState
            candidateName={candidate.name}
            jdName={jdName ?? "(unknown job)"}
          />
        ) : null}
        {phase === "loaded" && parsed ? (
          <>
            <EvaluationCards parsed={parsed} />
            {/* B-12: AI rating widget — sibling under EvaluationCards, loaded phase only */}
            <div className="flex justify-end px-content-px pb-3 pt-1">
              <AiRatingWidget
                actionType="ai_evaluation"
                resourceKey={row ? `ai_evaluation:${row.id}` : ""}
                modelUsed={row?.model_used ?? null}
              />
            </div>
          </>
        ) : null}
        {phase === "error" ? (
          <EvalErrorState onRetry={() => evalQ.refetch()} />
        ) : null}
      </div>
    </div>
  );
}

// ── Top action bar ───────────────────────────────────────────────────────────

interface EvaluationTopBarProps {
  jdName: string | undefined;
  showGenerate: boolean;
  isGenerating: boolean;
  canGenerate: boolean;
  onGenerate: () => void;
}

function EvaluationTopBar({
  jdName,
  showGenerate,
  isGenerating,
  canGenerate,
  onGenerate,
}: EvaluationTopBarProps): JSX.Element {
  return (
    <header
      className="flex flex-wrap items-center justify-between gap-3 border-b border-border-default px-content-px py-3.5"
      data-testid="ai-eval-topbar"
    >
      <div className="flex flex-col gap-0.5">
        <h2 className="text-sm font-semibold text-text-primary">
          Pre-Interview AI Evaluation
        </h2>
        <p className="text-xs text-text-tertiary">
          Evaluated against{" "}
          <span className="font-medium text-text-secondary">
            {jdName ?? "(unknown job)"}
          </span>
        </p>
      </div>
      {/* Generate button — shown only in first-run states (no-jd/empty/
          generating/error). Hidden once an analysis exists (`loaded`, the
          artifact is deterministic so no re-run affordance) AND while the
          query is still settling (`loading`) so it never flashes for a frame
          before existing content lands (FIX 1). */}
      {showGenerate ? (
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={onGenerate}
            loading={isGenerating}
            disabled={!canGenerate || isGenerating}
            data-testid="ai-eval-generate"
            className="px-3 py-1.5 text-sm"
          >
            <span className="inline-flex items-center gap-1.5">
              <Sparkles size={14} aria-hidden />
              {isGenerating ? "Generating…" : "Generate AI Evaluation"}
            </span>
          </Button>
        </div>
      ) : null}
    </header>
  );
}

// ── State surfaces ──────────────────────────────────────────────────────────

function NoJdEmptyState(): JSX.Element {
  return (
    <CenteredEmpty data-testid="ai-eval-no-jd">
      <Briefcase size={48} className="mb-4 text-text-tertiary" aria-hidden />
      <h3 className="mb-1.5 text-base font-semibold text-text-primary">
        No job context
      </h3>
      <p className="max-w-md text-sm text-text-secondary">
        Open this candidate from a job pipeline to see their AI evaluation.
      </p>
    </CenteredEmpty>
  );
}

interface EmptyStateProps {
  jdName: string | undefined;
  canGenerate: boolean;
  onGenerate: () => void;
}

function EmptyState({
  jdName,
  canGenerate,
  onGenerate,
}: EmptyStateProps): JSX.Element {
  return (
    <CenteredEmpty data-testid="ai-eval-empty">
      <Sparkles size={48} className="mb-4 text-text-tertiary" aria-hidden />
      <h3 className="mb-1.5 text-base font-semibold text-text-primary">
        No AI evaluation yet
      </h3>
      <p className="mb-5 max-w-md text-sm text-text-secondary">
        Generate a pre-interview AI evaluation against{" "}
        <span className="font-medium text-text-secondary">
          {jdName ?? "the selected job"}
        </span>
        . Takes up to a minute — and runs in the background.
      </p>
      <Button
        variant="primary"
        onClick={onGenerate}
        disabled={!canGenerate}
        data-testid="ai-eval-empty-generate"
      >
        <span className="inline-flex items-center gap-2">
          <Sparkles size={16} aria-hidden />
          Generate AI Evaluation
        </span>
      </Button>
    </CenteredEmpty>
  );
}

interface GeneratingStateProps {
  candidateName: string;
  jdName: string;
}

function GeneratingState({ candidateName, jdName }: GeneratingStateProps): JSX.Element {
  return (
    <div
      className="scroll-primary flex flex-1 flex-col overflow-x-hidden overflow-y-auto px-content-px py-5"
      data-testid="ai-eval-generating"
    >
      <EvaluationProgressBanner candidateName={candidateName} jdName={jdName} />
      <EvaluationCardsSkeleton />
    </div>
  );
}

interface EvalErrorStateProps {
  onRetry: () => void;
}

function EvalErrorState({ onRetry }: EvalErrorStateProps): JSX.Element {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center px-content-px py-6 text-center"
      data-testid="ai-eval-error"
    >
      <div className="flex max-w-md items-start gap-3 rounded-md border border-status-rejected-ring bg-status-rejected-bg p-3.5 text-status-rejected-fg">
        <AlertTriangle
          size={16}
          className="mt-0.5 flex-shrink-0"
          aria-hidden
        />
        <div className="text-left">
          <p className="mb-0.5 text-sm font-semibold">
            Couldn&apos;t load AI evaluation
          </p>
          <p className="text-xs">
            Generation failed. Try again or contact support if it persists.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 text-xs underline underline-offset-2 hover:no-underline"
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helper ──────────────────────────────────────────────────────────────────

function CenteredEmpty({
  children,
  ...rest
}: { children: ReactNode } & Record<string, unknown>): JSX.Element {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center px-content-px py-6 text-center"
      {...rest}
    >
      {children}
    </div>
  );
}
